// probably timing problem with lit: https://github.com/lit/lit/blob/7453e365000e6a289c139cf7e175a4742296333d/packages/reactive-element/src/css-tag.ts#L10
// if lit is loaded before style sheet it cannot handle CSSStyleSheets from AdornisCSS
import 'construct-style-sheets-polyfill'; // THIS HAS TO BE THE FIRST IMPORT FOR FIREFOX TO WORK CORRECTLY

import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { initBuchungsheldAuthentication } from '@adornis/buchungsheld-db/authentication/buchungsheld-authentication.js';
import { seedClientTranslationDictionary } from '@adornis/buchungsheld-db/translations/seed-client-dictionary.js';
import '@adornis/buchungsheld-theme/index.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.css';
import '@adornis/chemistry/elements/components/x-loader.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import '@adornis/mails/client/x-mail-editor.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { Language } from '@adornis/translation-core/languages.js';
import { subscribeTranslationDictionaryAndUpdateClientTranslator } from '@adornis/translation/db/translation-entry.js';
import { currentUser, subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import '@adornis/writer/x-tinymce.js';
import { html } from 'lit';
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import { setLocale } from '../../localization/out/runtime.js';
import { getAndEnsureUserDataOfUserID, touchMyActivity } from '../api/user.js';
import '../api/index.js';
import '../db/index.js';
import { subscribeMatchingWeekSettings } from '../api/settings.js';
import '../db/user.js';
import { initDateLocale } from './date-locale.js';
import './fonts/fonts.css';
import { mainRouter } from './mainRouter.js';
import { refreshPageTitles } from './page-title.js';
import { applyMatchingWeekColors } from './theme-colors.js';
import { setupParaglideTranslationContext } from './translation-context-setup.js';

import './tailwind.css' with { type: 'tailwind' };

// legacy stuff
DesignSystem._currentTheme.getValue()!.baseFontSize = 14;

initDateLocale();

BaseQLClient.initDefault();

// Align the login screen (chemistry accent) with the matching-week primary
// color right away, then keep both styling systems in sync with the
// admin-configured colors.
applyMatchingWeekColors();
subscribeMatchingWeekSettings()({ _id: 1, primaryColor: 1, primaryContentColor: 1 }).subscribe(settings =>
  applyMatchingWeekColors(settings),
);

void subscribeCurrentUser(() => ({
  firstName: 1,
  lastName: 1,
  email: 1,
  emailConfirmed: 1,
  roles: {
    _id: 1,
    _class: 1,
    name: 1,
    contexts: 1,
    permissions: 1,
  },
}));

seedClientTranslationDictionary();
currentUser()
  .pipe(
    filter(user => !!user?._id),
    map(user => user!._id),
    distinctUntilChanged(),
  )
  .subscribe(() => {
    void touchMyActivity()().catch(() => {});
  });

subscribeTranslationDictionaryAndUpdateClientTranslator();
globalLanguageSubject.subscribe(language => {
  setLocale(language === Language.GERMAN ? 'de' : 'en');
  refreshPageTitles();
});

combineLatest([globalLanguageSubject, currentUser()]).subscribe(async ([language, user]) => {
  if (!user) return;
  const profile = await getAndEnsureUserDataOfUserID(user._id)({ chosenLanguage: 1 });

  if (profile.chosenLanguage === language) return; // dont do anything if we don't need to
  profile.chosenLanguage = language;
  try {
    await profile.save();
  } catch (e) {
    console.warn(e);
  }
});

setupParaglideTranslationContext();

void initBuchungsheldAuthentication()
  .then(() =>
    initApp(() => {
      useObservable(() => globalLanguageSubject, []);
      return html` ${mainRouter.outlet.value}`;
    }),
  )
  .then(() => refreshPageTitles());
